import { Fade } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { ReactElement, ReactNode, Suspense, useMemo } from 'react';
import Center from 'src/components/Center';
import DILogo from 'src/components/DILogo';
import FullPageError from 'src/components/FullPageError';
import Header from 'src/components/Header';
import useAuth from 'src/hooks/useAuth';
import LoadingIndicator from 'src/lib/components/LoadingIndicator';
import BaseLayout from 'src/routes/layouts/BaseLayout';

import {
	AiAssistantProvider,
	Masthead,
	useAiService,
} from '@barracuda-internal/bds-core-v2';
import { enUSSuggestions } from 'src/utils/AiassistantConstants';
import { useAppSelector } from 'src/hooks/useAppSelector';
import jwtDecode from 'jwt-decode';
import MastheadActions from './MastheadActions';

const useStyles = makeStyles({
	root: {
		// to keep Intercom from being over any buttons
		//paddingBottom: '88px',
	},
});
interface AppLayoutProps {
	env: string;
	children?: ReactNode;
}

function AppLayout({ env, children }: AppLayoutProps): ReactElement {
	const { auth, error } = useAuth();
	const { pageTitle } = useAppSelector((_store) => ({
		pageTitle: _store.masthead.pageTitle || '',
	}));
	const oidc_access_token_decoded = jwtDecode(auth.oidcUser.access_token);
	const ext_at = oidc_access_token_decoded['ext_at'];

	const hostname = useMemo(() => {
		const normalizedEnv = (env || '').toLowerCase();
		return normalizedEnv === 'prod' || normalizedEnv === 'prodin'
			? 'https://api.app.barracudanetworks.com'
			: 'https://qa.dev01.useast2.nexus.aws.cudaops.com';
	}, [env]);

	const aiAssistant = useAiService({
		aiServiceBaseUrl: hostname,
		getToken: () => ext_at,
		productName: 'Data Inspector',
	});

	const classes = useStyles();

	if (!auth)
		return (
			<BaseLayout>
				<Center>
					<Fade in={true} timeout={2500}>
						<div>
							<>
								<DILogo lightVersion={false} size={512} />
								{error ? (
									<FullPageError message="Authorization failed." />
								) : (
									<LoadingIndicator
										message="Authorizing..."
										loaderSize={100}
									/>
								)}
							</>
						</div>
					</Fade>
				</Center>
			</BaseLayout>
		);

	return (
		<BaseLayout>
			<AiAssistantProvider
				{...aiAssistant}
				suggestions={enUSSuggestions}
				bdsDrawerType="temporary"
			>
				<div className={classes.root}>
					<nav style={{ position: 'relative' }}>
						<Header />
						<Masthead
							productTitle={'Data Inspector'}
							pageTitle={pageTitle}
							rightContent={<MastheadActions />}
						/>
					</nav>
					<article>
						<Suspense
							fallback={
								<LoadingIndicator
									message="Loading..."
									loaderSize={100}
								/>
							}
						>
							{children}
						</Suspense>
					</article>
				</div>
			</AiAssistantProvider>
		</BaseLayout>
	);
}

export default AppLayout;
